import {useCallback, useMemo} from "react";
import {useSelector} from "react-redux";

import Features from "Resources/Features.js";

export default () => {

	const {features, featuresAvailable, featuresForced} = useSelector(store => store);


	/**
	 * Get whether we have a given feature by name.
	 * 
	 * @param {String} feature
	 * @return {Boolean}
	 */
	const isFeatureEnabled = useCallback(featureName => {

		/** Get a list of enabled features, and force enabled features */
		const allFeatures = [
			...features,
			...featuresForced
		];

		/** Get the object for the requested feature */
		const featureObject = Features.find(f => f.name === featureName);

		/** Feature must exist! */
		if (!featureObject) return false;

		/** Determine if the requested feature is included in the list */
		const featureEnabled = allFeatures.includes(featureObject.name);

		/** Determine if the requested feature has a parent feature that must also be enabled */
		if (featureObject.parentFeature) {
			return featureEnabled && isFeatureEnabled(featureObject.parentFeature);
		}
		else {
			return featureEnabled;
		}

	}, [features, featuresForced]);


	/**
	 * Get whether a given feature is a live feature by name.
	 *
	 * @param {String} feature
	 * @return {Boolean}
	 */
	const isLiveFeature = useCallback(feature => features.includes(feature), [features]);


	/**
	 * Get whether a given feature is a preview feature by name.
	 * 
	 * @param {String} feature
	 * @return {Boolean}
	 */
	const isPreviewFeature = useCallback(feature => (featuresAvailable[feature] === false), [featuresAvailable]);


	/**
	 * Get all available feature objects.
	 * 
	 * @return {Array}
	 */
	const availableFeatureObjects = useMemo(() => Features.filter(({name}) => (isFeatureEnabled(name) || isPreviewFeature(name))), [isFeatureEnabled, isPreviewFeature]);


	/**
	 * Get all enabled feature objects.
	 * 
	 * @return {Array}
	 */
	const enabledFeatureObjects = useMemo(() => Features.filter(f => isFeatureEnabled(f.name)), [isFeatureEnabled]);


	return useMemo(() => {
		return {
			features: enabledFeatureObjects,
			featuresAvailable: availableFeatureObjects,
			featuresForced,
			isFeatureEnabled,
			isLiveFeature,
			isPreviewFeature
		};
	}, [
		enabledFeatureObjects,
		availableFeatureObjects,
		featuresForced,
		isFeatureEnabled,
		isLiveFeature,
		isPreviewFeature
	]);

};
